import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';
import { createDescendantContext, createDescendantsStepper, DescendantsProvider, useDescendantsContextSelector, useDescendantsStepper, } from '../Descandants';
import { aggregate, useComposedRefs, useControlledValue, useEventListener, suffixId, useFloatingElement, useAnimation, flattenChildren, getSide, useIsomorphicLayoutEffect as useLayoutEffect, useCurrent, } from '../../utils';
import { labelId, validationTextId, helperTextId } from '../FormField';
import { Icon } from '../Icon';
import ChevronDown from '@mollie/ui-assets/icons/utilitarian/ChevronDown';
import Search from '@mollie/ui-assets/icons/utilitarian/Search';
import { Portal } from '../Portal';
import { TextField } from '../TextField';
import { Button } from '../Button';
import { ComboBoxContext } from './ComboBoxContext';
import { ComboBoxSeparator } from './ComboBoxSeparator';
import { ComboBoxItem } from './ComboBoxItem';
import { ComboBoxAddItem } from './ComboBoxAddItem';
import { ComboBoxEmptyMessage } from './ComboBoxEmptyMessage';
import { useModalContext } from '../Modal';
import { ComboBoxHeader } from './ComboBoxHeader';
import { TagField } from '../TagField';
import './ComboBox.css';
export const ComboBoxDescendantsContext = createDescendantContext('ComboBoxDescendantsContext');
const comboBoxDescendantsStepper = createDescendantsStepper();
var KeyboardActions;
(function (KeyboardActions) {
    KeyboardActions[KeyboardActions["OPEN"] = 0] = "OPEN";
    KeyboardActions[KeyboardActions["OPEN_FIRST"] = 1] = "OPEN_FIRST";
    KeyboardActions[KeyboardActions["CLOSE"] = 2] = "CLOSE";
    KeyboardActions[KeyboardActions["SELECT"] = 3] = "SELECT";
    KeyboardActions[KeyboardActions["TYPE"] = 4] = "TYPE";
    KeyboardActions[KeyboardActions["FIRST"] = 5] = "FIRST";
    KeyboardActions[KeyboardActions["LAST"] = 6] = "LAST";
    KeyboardActions[KeyboardActions["NEXT"] = 7] = "NEXT";
    KeyboardActions[KeyboardActions["PREVIOUS"] = 8] = "PREVIOUS";
})(KeyboardActions || (KeyboardActions = {}));
function ComboBoxComponent(props) {
    var _a, _b;
    const { children, prefix, id: userId, placeholder, label, validationText, helperText, onChange, onClear: userOnClear, onSelectAll: userOnSelectAll, onInput, onBlur, onAfterClose, value: userValue, defaultValue, inputValue, name, multiselect, clearLabel = 'Clear', selectAllLabel = 'Select all', selectedLabel = (size) => `${size} selected`, ariaShowListboxLabel = 'Show listbox', inputRef: userInputRef, loading, emptyMessage, closeOnSelect = true, activeIndexApiRef, UNSAFE_labelCache = {}, ...rest } = props;
    const { initialized: modalInitialized } = useModalContext();
    const labelCache = useRef(UNSAFE_labelCache);
    const [expanded, setExpanded] = useState(false);
    const descendants = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ descendants }) => descendants);
    const selected = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ selected }) => selected);
    const setActiveIndex = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ setActiveIndex }) => setActiveIndex);
    const getActiveIndex = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ getActiveIndex }) => getActiveIndex);
    const setSelected = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ setSelected }) => setSelected);
    const getSelected = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ getSelected }) => getSelected);
    const getDescendants = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ getDescendants }) => getDescendants);
    const getLabel = useCallback((value, multiselect) => {
        if (multiselect) {
            return Array.isArray(value)
                ? value
                    .map((value) => getLabel(value))
                    .filter((value) => !!value)
                    .join(', ')
                : '';
        }
        if (value == null) {
            return '';
        }
        const descendant = getDescendants().find(({ value: dv }) => dv === value);
        if (multiselect && (descendant === null || descendant === void 0 ? void 0 : descendant.disabled)) {
            return '';
        }
        if (descendant) {
            labelCache.current[value] = descendant.label || value;
        }
        return labelCache.current[value] || String(value);
    }, [getDescendants]);
    const getValue = useCallback((label) => { var _a; return ((_a = getDescendants().find(({ label: dl }) => dl === label)) === null || _a === void 0 ? void 0 : _a.value) || label; }, [getDescendants]);
    const [value, setValue] = useControlledValue({
        value: userValue,
        onChange: (value) => onChange === null || onChange === void 0 ? void 0 : onChange(value, getLabel(value, multiselect)),
        defaultValue: (multiselect
            ? Array.isArray(defaultValue)
                ? defaultValue
                : defaultValue
                    ? [defaultValue]
                    : []
            : defaultValue),
    });
    const selectedDescendant = useDescendantsContextSelector(ComboBoxDescendantsContext, ({ descendants }) => descendants.find(({ value: dv }) => dv === value));
    const currentValue = useCurrent(value);
    useLayoutEffect(() => {
        var _a, _b;
        const selected = descendants.filter((descendant) => {
            var _a;
            if (typeof value === 'undefined') {
                return false;
            }
            if (Array.isArray(value)) {
                return value.includes((_a = descendant.value) !== null && _a !== void 0 ? _a : '');
            }
            return descendant.value === value;
        });
        setSelected((currentSelected) => {
            if (currentSelected.length === selected.length &&
                currentSelected.every((element, index) => {
                    return element === selected[index];
                })) {
                return currentSelected;
            }
            return selected;
        });
        if (getActiveIndex() === -1) {
            setActiveIndex((_b = (_a = selected[selected.length - 1]) === null || _a === void 0 ? void 0 : _a.index) !== null && _b !== void 0 ? _b : -1);
        }
    }, [descendants, getActiveIndex, setActiveIndex, setSelected, value]);
    const stepper = useDescendantsStepper(ComboBoxDescendantsContext, comboBoxDescendantsStepper);
    const { anchor, floating, floatingStyles, anchorPoint } = useFloatingElement({
        placement: 'bottom-start',
        preferFlip: true,
        size: ({ elements }) => {
            var _a, _b;
            const { width } = elements.anchor.getBoundingClientRect();
            let minHeight = '0';
            const optionsElements = elements.floating.querySelectorAll('[role="option"]');
            let optionHeight = ((_a = optionsElements[0]) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0;
            if (optionsElements[0] && optionsElements[0].tagName === 'INPUT') {
                optionHeight = ((_b = optionsElements[0].parentElement) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
            }
            if (optionsElements.length === 1) {
                minHeight = `${optionHeight}px`;
            }
            if (optionsElements.length > 1) {
                minHeight = `${Math.min(optionsElements.length, 3) * optionHeight}px`;
            }
            return {
                width: `${width}px`,
                maxHeight: optionHeight
                    ? `${5 * optionHeight + optionHeight / 2}px`
                    : 'none',
                minHeight,
            };
        },
    });
    const animationAnchor = getSide(anchorPoint !== null && anchorPoint !== void 0 ? anchorPoint : 'bottom');
    const [anchorElement, setAnchorElement] = useState(null);
    const controlRef = useRef(null);
    const inputRef = useRef(null);
    const animationRef = useRef(null);
    const lastUsedKeyRef = useRef(null);
    const searchTimeout = useRef();
    const searchString = useRef('');
    const preventBlur = useRef(false);
    const cachedChildren = useRef(children);
    const composedControlRef = useComposedRefs(floating, controlRef);
    const composedInputRef = useComposedRefs(inputRef, userInputRef, setAnchorElement);
    const { canMount, animationClassNames } = useAnimation(animationRef, expanded, {
        onAfterMount: () => {
            var _a, _b;
            if (!multiselect) {
                (_b = (_a = getSelected()[0]) === null || _a === void 0 ? void 0 : _a.element) === null || _b === void 0 ? void 0 : _b.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        },
        onAfterAnimationEnded: () => {
            if (!expanded) {
                onAfterClose === null || onAfterClose === void 0 ? void 0 : onAfterClose(currentValue.current, getLabel(currentValue.current, multiselect));
            }
        },
    });
    const uniqueId = useId();
    const id = userId !== null && userId !== void 0 ? userId : uniqueId;
    const describedBy = aggregate(validationText ? validationTextId(id) : undefined, helperText ? helperTextId(id) : undefined);
    const controlId = suffixId(id, 'control');
    useEventListener(window, 'click', (e) => {
        var _a;
        if (!((_a = controlRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            setExpanded(false);
        }
    });
    const maybeClose = useCallback(() => {
        var _a;
        if (closeOnSelect && !multiselect) {
            setExpanded(false);
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [closeOnSelect, multiselect]);
    const onSelect = useCallback((descendant) => {
        if (!descendant)
            return;
        cachedChildren.current = children;
        setValue((prevValue) => {
            var _a;
            let nextValue = descendant.value;
            if (Array.isArray(prevValue)) {
                if (prevValue.includes((_a = descendant.value) !== null && _a !== void 0 ? _a : '')) {
                    nextValue = prevValue.filter((value) => value !== descendant.value);
                }
                else {
                    nextValue = [...prevValue, descendant.value];
                }
            }
            return nextValue;
        });
        maybeClose();
    }, [children, maybeClose, setValue]);
    const onClear = () => {
        var _a;
        if (!Array.isArray(value))
            return;
        setValue((value) => {
            if (Array.isArray(value)) {
                return getDescendants()
                    .filter((descedant) => { var _a; return descedant.disabled && value.includes((_a = descedant.value) !== null && _a !== void 0 ? _a : ''); })
                    .map((descedant) => descedant.value);
            }
            return value;
        });
        if (userOnClear) {
            userOnClear(value);
        }
        setExpanded(false);
        setActiveIndex(-1);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const onSelectAll = () => {
        var _a;
        setValue((value) => {
            if (Array.isArray(value)) {
                return getDescendants()
                    .filter((descedant) => !descedant.disabled)
                    .map((descedant) => descedant.value);
            }
            return value;
        });
        if (userOnSelectAll) {
            userOnSelectAll(value);
        }
        setExpanded(false);
        setActiveIndex(-1);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const getSearchString = (char) => {
        if (typeof searchTimeout.current === 'number') {
            window.clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = window.setTimeout(() => {
            searchString.current = '';
        }, 500);
        searchString.current += char;
        return searchString.current;
    };
    const setActiveDescendantIndexByMatch = useCallback((match, options) => {
        var _a, _b;
        const foundDescendantIndex = (_b = (_a = stepper.getMatchedDescendant((descendant) => {
            var _a, _b;
            return String((_b = (_a = descendant.label) !== null && _a !== void 0 ? _a : descendant.value) !== null && _b !== void 0 ? _b : '')
                .toLowerCase()
                .indexOf(match.toLowerCase()) === 0;
        })) === null || _a === void 0 ? void 0 : _a.index) !== null && _b !== void 0 ? _b : -1;
        if (foundDescendantIndex > -1) {
            if (options === null || options === void 0 ? void 0 : options.expand)
                setExpanded(true);
            setActiveIndex(foundDescendantIndex);
        }
    }, [setActiveIndex, stepper]);
    const onKeyDown = (e) => {
        var _a, _b, _c, _d;
        const { key, altKey, ctrlKey, metaKey } = e;
        lastUsedKeyRef.current = e.key;
        // handle regular action on return and space key when target is a button
        if (((_a = e.target.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'button' &&
            (key === 'Enter' || key === ' ')) {
            return;
        }
        const actionKey = (() => {
            const keyOpen = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'];
            // when not allowing search use space to open the listbox
            if (!onInput) {
                keyOpen.push(' ');
            }
            if (!expanded && keyOpen.includes(key)) {
                return key === 'ArrowUp'
                    ? KeyboardActions.OPEN_FIRST
                    : KeyboardActions.OPEN;
            }
            if (key === 'Home')
                return KeyboardActions.FIRST;
            if (key === 'End')
                return KeyboardActions.LAST;
            const keyTyping = key === 'Backspace' ||
                key === 'Clear' ||
                (key.length === 1 && !altKey && !ctrlKey && !metaKey);
            const allowSpaceWhenTyping = key !== ' ' || (key === ' ' && searchString.current.length > 0);
            if (!onInput && keyTyping && allowSpaceWhenTyping) {
                return KeyboardActions.TYPE;
            }
            if (expanded) {
                const returnOrSpace = onInput
                    ? key === 'Enter'
                    : key === 'Enter' || key === ' ';
                if (key === 'ArrowUp' && altKey)
                    return KeyboardActions.SELECT;
                if (key === 'ArrowDown' && !altKey)
                    return KeyboardActions.NEXT;
                if (key === 'ArrowUp')
                    return KeyboardActions.PREVIOUS;
                if (key === 'Escape')
                    return KeyboardActions.CLOSE;
                if (returnOrSpace)
                    return KeyboardActions.SELECT;
            }
            return;
        })();
        if (actionKey) {
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        switch (actionKey) {
            case KeyboardActions.OPEN: {
                e.preventDefault();
                setExpanded(true);
                break;
            }
            case KeyboardActions.OPEN_FIRST: {
                e.preventDefault();
                setExpanded(true);
                const firstDescendant = stepper.getFirstDescendant();
                if (firstDescendant) {
                    setActiveIndex(firstDescendant.index);
                }
                break;
            }
            case KeyboardActions.CLOSE: {
                e.preventDefault();
                setExpanded(false);
                (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.focus();
                break;
            }
            case KeyboardActions.SELECT: {
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                (_d = activeDescendant === null || activeDescendant === void 0 ? void 0 : activeDescendant.element) === null || _d === void 0 ? void 0 : _d.click();
                break;
            }
            case KeyboardActions.TYPE: {
                e.preventDefault();
                const searchString = getSearchString(key);
                setActiveDescendantIndexByMatch(searchString);
                break;
            }
            case KeyboardActions.FIRST: {
                e.preventDefault();
                const firstDescendant = stepper.getFirstDescendant();
                if (firstDescendant) {
                    setActiveIndex(firstDescendant.index);
                }
                break;
            }
            case KeyboardActions.LAST: {
                e.preventDefault();
                const lastDescendant = stepper.getLastDescendant();
                if (lastDescendant) {
                    setActiveIndex(lastDescendant.index);
                }
                break;
            }
            case KeyboardActions.NEXT: {
                e.preventDefault();
                const nextDescendant = stepper.getNextDescendant();
                if (nextDescendant) {
                    setActiveIndex(nextDescendant.index);
                }
                break;
            }
            case KeyboardActions.PREVIOUS: {
                e.preventDefault();
                const previousDescendant = stepper.getPreviousDescendant();
                if (previousDescendant) {
                    setActiveIndex(previousDescendant.index);
                }
                break;
            }
        }
    };
    useLayoutEffect(() => {
        if (inputValue) {
            setActiveDescendantIndexByMatch(inputValue, {
                expand: false,
            });
        }
    }, [inputValue, setActiveDescendantIndexByMatch]);
    useLayoutEffect(() => {
        if (multiselect && !Array.isArray(value)) {
            setValue([value]);
        }
        else if (!multiselect && Array.isArray(value)) {
            setValue(value.pop());
        }
    }, [multiselect, setValue, value]);
    const activeIndexApi = useMemo(() => ({
        get() {
            return getActiveIndex();
        },
        set(index) {
            setActiveIndex(index);
        },
    }), [getActiveIndex, setActiveIndex]);
    if (activeIndexApiRef) {
        activeIndexApiRef.current =
            activeIndexApi;
    }
    const selectedNotDisabled = selected.filter((descendant) => !descendant.disabled);
    const usedChildren = !expanded && canMount ? cachedChildren.current : children;
    const flatUsedChildren = flattenChildren(usedChildren);
    const displayValue = useMemo(() => multiselect
        ? Array.isArray(value)
            ? value.map((value) => getLabel(value))
            : [value]
        : getLabel(value), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, multiselect, descendants]);
    const trulyExpanded = Boolean(expanded && children);
    const Element = multiselect ? TagField : TextField;
    const elementProps = {};
    if (multiselect) {
        elementProps.value = displayValue;
        elementProps.placeholder =
            !Array.isArray(value) || value.length === 0 ? placeholder : undefined;
        elementProps.readOnly = onInput ? false : true;
        elementProps.onDismiss = (label) => {
            setValue((prevValue) => prevValue.filter((optionValue) => optionValue !== getValue(label)));
        };
        elementProps.onInput = (e) => {
            onInput === null || onInput === void 0 ? void 0 : onInput(e.target.value);
        };
        elementProps.inputValue = inputValue;
        elementProps.noWrap = true;
    }
    else {
        elementProps.value = (_a = inputValue !== null && inputValue !== void 0 ? inputValue : displayValue) !== null && _a !== void 0 ? _a : '';
        elementProps.placeholder = placeholder;
    }
    const floatingZIndex = useMemo(() => anchorElement
        ? getComputedStyle(anchorElement).getPropertyValue('--mollie-ui-floating-z-index')
        : undefined, [anchorElement]);
    return (React.createElement(ComboBoxContext.Provider, { value: {
            onSelect,
            maybeClose,
            lastUsedKeyRef,
            value,
            multiselect,
        } },
        name && Array.isArray(value) ? (value.map((val) => (React.createElement("input", { type: "hidden", name: `${name}[]`, value: val != null ? String(val) : undefined })))) : (React.createElement("input", { type: "hidden", name: name, value: value != null ? String(value) : undefined })),
        React.createElement(Element, { ...rest, ...elementProps, role: "combobox", autoComplete: "off", spellCheck: "false", label: label, prefix: prefix !== null && prefix !== void 0 ? prefix : selectedDescendant === null || selectedDescendant === void 0 ? void 0 : selectedDescendant.prefix, validationText: validationText, helperText: helperText, id: id, "aria-expanded": trulyExpanded, "aria-controls": controlId, "aria-haspopup": "listbox", "aria-autocomplete": onInput ? 'list' : undefined, "aria-activedescendant": trulyExpanded ? (_b = stepper.getActiveDescendant()) === null || _b === void 0 ? void 0 : _b.id : undefined, onInput: (e) => {
                cachedChildren.current = children;
                if (onInput) {
                    flushSync(() => {
                        onInput(e.currentTarget.value);
                    });
                    setExpanded((expanded) => getDescendants().length > 0 ? true : !!emptyMessage && !!expanded);
                }
            }, onClick: (e) => {
                if (flatUsedChildren.length === 0) {
                    return;
                }
                e.stopPropagation();
                setExpanded((expanded) => !expanded);
                setActiveIndex(-1);
            }, onKeyDown: (e) => {
                cachedChildren.current = children;
                if (e.key !== 'Tab') {
                    preventBlur.current = true;
                }
                return onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
            }, onKeyUp: () => {
                // queue until all other handlers are done executing
                setTimeout(() => {
                    preventBlur.current = false;
                }, 0);
            }, onMouseDown: (e) => {
                cachedChildren.current = children;
                if (e.currentTarget.value === '') {
                    e.preventDefault();
                    e.currentTarget.focus();
                }
                lastUsedKeyRef.current = null;
            }, onSelect: (e) => {
                if (!onInput) {
                    e.currentTarget.setSelectionRange(null, null);
                }
            }, onFocus: (e) => {
                if (!onInput) {
                    e.currentTarget.setSelectionRange(null, null);
                }
                else {
                    const input = e.currentTarget;
                    setTimeout(() => {
                        input.setSelectionRange(-1, -1);
                    }, 0);
                }
            }, onBlur: () => {
                cachedChildren.current = children;
                if (!preventBlur.current) {
                    setExpanded(false);
                    if (onBlur)
                        onBlur(currentValue.current, getLabel(currentValue.current, multiselect));
                }
            }, className: "mollie-ui-combo-box", UNSAFE_inputClassName: clsx({
                'mollie-ui-combo-box-input--search': onInput,
            }), UNSAFE_textFieldClassName: "mollie-ui-combo-box-text-field", inputRef: composedInputRef, UNSAFE_textFieldRef: anchor, loading: loading, postfix: flatUsedChildren.length > 0 && (React.createElement(Button, { unstyled: true, tabIndex: -1, "aria-label": ariaShowListboxLabel, "aria-expanded": trulyExpanded, "aria-controls": controlId, "aria-haspopup": "listbox", onClick: (e) => {
                    var _a;
                    e.stopPropagation();
                    setExpanded((expanded) => !expanded);
                    setActiveIndex(-1);
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                } },
                React.createElement(Icon, { src: ChevronDown }))), "data-1p-ignore": true }),
        React.createElement(Portal, null,
            React.createElement("div", { className: clsx('mollie-ui-combo-box-listbox', {
                    'mollie-ui-combo-box-listbox--expanded': canMount,
                }), role: "listbox", id: controlId, "aria-labelledby": label ? labelId(id) : id, "aria-describedby": aggregate(multiselect ? suffixId(controlId, 'selected') : undefined, describedBy) || undefined, "aria-multiselectable": multiselect ? 'true' : 'false', ref: composedControlRef, style: {
                    ...floatingStyles,
                    '--mollie-ui-floating-z-index': modalInitialized
                        ? 600
                        : floatingZIndex,
                }, tabIndex: -1, onMouseDown: (e) => {
                    cachedChildren.current = children;
                    e.preventDefault();
                    preventBlur.current = true;
                    lastUsedKeyRef.current = null;
                }, onMouseUp: () => {
                    // queue until all other handlers are done executing
                    setTimeout(() => {
                        preventBlur.current = false;
                    }, 0);
                }, onKeyDown: (e) => {
                    cachedChildren.current = children;
                    preventBlur.current = true;
                    return onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
                }, onKeyUp: () => {
                    // queue until all other handlers are done executing
                    setTimeout(() => {
                        preventBlur.current = false;
                    }, 0);
                } }, flatUsedChildren.length > 0 ? (React.createElement("div", { className: clsx('mollie-ui-combo-box-control', {
                    [`mollie-ui-animation-anchor-${animationAnchor}`]: animationAnchor,
                }, animationClassNames), ref: animationRef },
                multiselect && (React.createElement("div", { className: "mollie-ui-combo-box-control-actions" },
                    React.createElement("span", { id: suffixId(controlId, 'selected') }, selectedLabel(selectedNotDisabled.length)),
                    selectedNotDisabled.length > 0 ? (React.createElement(Button, { variant: "tertiary-inline", size: "small", marginInlineStart: "auto", onClick: (e) => {
                            e.stopPropagation();
                            onClear();
                        } }, clearLabel)) : (React.createElement(Button, { variant: "tertiary-inline", size: "small", marginInlineStart: "auto", onClick: (e) => {
                            e.stopPropagation();
                            onSelectAll();
                        } }, selectAllLabel)))),
                React.createElement("div", { className: "mollie-ui-combo-box-control-content" }, usedChildren))) : (emptyMessage && (React.createElement("div", { className: clsx('mollie-ui-combo-box-control', {
                    [`mollie-ui-animation-anchor-${animationAnchor}`]: animationAnchor,
                }, animationClassNames), ref: animationRef },
                React.createElement(ComboBoxEmptyMessage, null,
                    React.createElement(Icon, { src: Search }),
                    emptyMessage))))))));
}
export function ComboBox(props) {
    return (React.createElement(DescendantsProvider, { context: ComboBoxDescendantsContext },
        React.createElement(ComboBoxComponent, { ...props })));
}
ComboBox.Item = ComboBoxItem;
ComboBox.AddItem = ComboBoxAddItem;
ComboBox.Separator = ComboBoxSeparator;
ComboBox.EmptyMessage = ComboBoxEmptyMessage;
ComboBox.Header = ComboBoxHeader;
//# sourceMappingURL=ComboBox.js.map